import React, { useEffect, useState, useRef } from 'react';
import { catalog as catalogApi } from '@/utils/api';
import { Search, Plus, Tag, X } from 'lucide-react';
import { toast } from 'sonner';

/**
 * CatalogPicker — search the master catalog and pick an item, or add a new one on the fly.
 * onPick({id, name, description, default_price, default_hours, unit, category}).
 */
export default function CatalogPicker({ onPick, context = 'estimate' }) {
  const [q, setQ] = useState('');
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [newItem, setNewItem] = useState({ name: '', category: 'general', default_price: 100, default_hours: 1, description: '' });
  const timerRef = useRef(null);
  const wrapperRef = useRef(null);

  useEffect(() => {
    const search = async () => {
      setLoading(true);
      try {
        const r = await catalogApi.list({ q: q || undefined, limit: 20 });
        setItems(r.data);
      } catch { setItems([]); }
      setLoading(false);
    };
    if (open) {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(search, 200);
    }
    return () => timerRef.current && clearTimeout(timerRef.current);
  }, [q, open]);

  // Close on outside click
  useEffect(() => {
    const onClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const handlePick = async (it) => {
    onPick(it);
    // Fire & forget usage count bump
    if (it.id) catalogApi.touch(it.id).catch(() => {});
    setOpen(false);
    setQ('');
  };

  const handleSaveNew = async () => {
    if (!newItem.name.trim()) return toast.error('Name required');
    try {
      const r = await catalogApi.create(newItem);
      toast.success('Added to catalog');
      handlePick(r.data);
      setAdding(false);
      setNewItem({ name: '', category: 'general', default_price: 100, default_hours: 1, description: '' });
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  return (
    <div className="relative" ref={wrapperRef} data-testid="catalog-picker">
      <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-btn text-xs cursor-pointer transition-colors" style={{ background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--muted)' }} onClick={() => setOpen(o => !o)} data-testid="catalog-picker-toggle">
        <Search size={11} /> Pick from catalog
      </div>

      {open && (
        <div className="absolute z-40 mt-1 w-[420px] rounded-card shadow-lg overflow-hidden" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
          {!adding ? (
            <>
              <div className="p-2 flex items-center gap-2" style={{ borderBottom: '1px solid var(--border)' }}>
                <Search size={13} style={{ color: 'var(--muted)' }} />
                <input autoFocus className="flex-1 bg-transparent outline-none text-sm" placeholder="Search tasks, tags, categories…" value={q} onChange={e => setQ(e.target.value)} data-testid="catalog-search-input" />
                <button onClick={() => { setAdding(true); setNewItem(ni => ({ ...ni, name: q })); }} className="text-xs flex items-center gap-1 px-2 py-1 rounded-btn" style={{ background: '#E6F1F4', color: '#006080' }} data-testid="catalog-add-new-button">
                  <Plus size={10} /> Add new
                </button>
              </div>
              <div className="max-h-72 overflow-y-auto">
                {loading ? (
                  <div className="p-4 text-center text-xs" style={{ color: 'var(--muted)' }}>Searching…</div>
                ) : items.length === 0 ? (
                  <div className="p-6 text-center">
                    <p className="text-xs mb-2" style={{ color: 'var(--muted)' }}>No items. Want to add one?</p>
                    <button onClick={() => { setAdding(true); setNewItem(ni => ({ ...ni, name: q })); }} className="text-xs px-3 py-1.5 rounded-btn" style={{ background: '#E6F1F4', color: '#006080' }}>
                      Create "{q || 'new item'}"
                    </button>
                  </div>
                ) : items.map(it => (
                  <button key={it.id} onClick={() => handlePick(it)} className="w-full text-left px-3 py-2.5 transition-colors hover:bg-[var(--bg)] flex items-center gap-3" style={{ borderBottom: '1px solid var(--border)' }} data-testid={`catalog-item-${it.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-semibold truncate" style={{ color: 'var(--ink)' }}>{it.name}</p>
                        {it.category && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded-sm flex items-center gap-0.5" style={{ background: '#E6F1F4', color: '#006080' }}>
                            <Tag size={8} /> {it.category}
                          </span>
                        )}
                      </div>
                      {it.description && <p className="text-xs truncate" style={{ color: 'var(--muted)' }}>{it.description}</p>}
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-sm font-bold tabular-nums" style={{ color: 'var(--ink)' }}>${it.default_price}</p>
                      <p className="text-[10px]" style={{ color: 'var(--muted)' }}>/{it.unit || 'hour'}</p>
                    </div>
                  </button>
                ))}
              </div>
            </>
          ) : (
            <div className="p-3 space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-sm font-bold" style={{ color: 'var(--ink)' }}>Add to catalog</p>
                <button onClick={() => setAdding(false)} style={{ color: 'var(--muted)' }}><X size={14} /></button>
              </div>
              <input className="input-agency text-sm" placeholder="Task name *" value={newItem.name} onChange={e => setNewItem({...newItem, name: e.target.value})} data-testid="new-catalog-name" />
              <div className="grid grid-cols-2 gap-2">
                <input className="input-agency text-sm" placeholder="Category (e.g. RPA, n8n)" value={newItem.category} onChange={e => setNewItem({...newItem, category: e.target.value})} data-testid="new-catalog-category" />
                <input className="input-agency text-sm" type="number" placeholder="Price/hour" value={newItem.default_price} onChange={e => setNewItem({...newItem, default_price: Number(e.target.value)})} data-testid="new-catalog-price" />
              </div>
              <textarea className="input-agency text-sm resize-none" rows={2} placeholder="Description (optional)" value={newItem.description} onChange={e => setNewItem({...newItem, description: e.target.value})} />
              <div className="flex gap-2">
                <button type="button" className="flex-1 py-1.5 text-xs rounded-btn border" style={{ borderColor: 'var(--border)', color: 'var(--muted)' }} onClick={() => setAdding(false)}>Cancel</button>
                <button type="button" className="flex-1 btn-primary py-1.5 text-xs" onClick={handleSaveNew} disabled={!newItem.name.trim()} data-testid="save-new-catalog-item">Save & Pick</button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
